import { useState } from "react";

/* PACKAGES */
import { motion } from "framer-motion";

/* MATERIAL TAILWIND COMPONENTS */
import { Button } from "@material-tailwind/react";

/* USER DEFINED COMPONENTS */
import ProductBox from "../../components/others/product";

/* ICONS */
import { FaRegHeart } from "react-icons/fa6";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";

/* SAMPLE DATA */
import bestsellers from "../../data/bestsellers";

/* ASSETS */
import Product01 from "../../assets/images/west-coast-products/product-01.jpeg";

/* CONTAINER ANIMATION */
const containerAnimation = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 1 },
  },
};

/* DIV ANIMATION */
const animation = {
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      damping: 12,
      delay: 1,
      stiffness: 100,
    },
  },
  hidden: {
    opacity: 0,
    y: 20,
    transition: {
      type: "spring",
      damping: 12,
      delay: 1,
      stiffness: 100,
    },
  },
};

const ProductDetail = () => {
  const [quantity, setQuantity] = useState(1);

  /* QUANTITY ADD HANDLER */
  const addQuantity = () => {
    setQuantity(quantity + 1);
  };

  /* QUALITY SUBTRACT HANDLER */
  const subtractQuantity = () => {
    if (quantity === 1) {
      setQuantity(1);
    } else {
      setQuantity(quantity - 1);
    }
  };

  /* QUANTITY BOX */
  const QuantityBox = (props) => {
    return (
      <div className="flex items-center">
        <Button
          className="h-[5vh] shadow-none hover:shadow-none bg-white hover:bg-black hover:bg-opacity-5 border border-r-0 border-greyBorder rounded-r-none rounded-l-lg py-2 px-3 flex items-center justify-center"
          onClick={subtractQuantity}
        >
          <IoIosArrowBack className="w-4 h-4 text-black" />
        </Button>
        <div className="w-[5vh] h-[5vh]">
          <input
            type="number"
            name="quantity"
            id="quantity"
            className="w-full h-full block font-source font-semibold placeholder:font-source placeholder:font-normal text-sm text-black p-0 focus:outline-none border border-greyBorder text-center"
            value={props.value}
            onChange={props.onChange}
          />
        </div>
        <Button
          className="h-[5vh] shadow-none hover:shadow-none bg-white hover:bg-black hover:bg-opacity-5 border border-l-0 border-greyBorder rounded-l-none rounded-r-lg py-2 px-3 flex items-center justify-center"
          onClick={addQuantity}
        >
          <IoIosArrowForward className="w-4 h-4 text-black" />
        </Button>
      </div>
    );
  };

  return (
    <motion.section
      variants={containerAnimation}
      initial="hidden"
      animate="visible"
      className="bg-white py-6 xl:py-14 lg:py-14"
    >
      <div className="container">
        {/* PRODUCT IMAGES AND OTHER DETAILS */}
        <motion.div
          variants={animation}
          className="grid grid-cols-1 xl:grid-cols-2 lg:grid-cols-2 gap-y-5 xl:gap-10 lg:gap-10"
        >
          {/* PRODUCT IMAGES */}
          <motion.div
            variants={animation}
            className="col-span-1 h-fit sticky top-5"
          >
            <div className="bg-greyBg rounded-xl shadow-inner p-5">
              <img
                src={Product01}
                className="w-full h-[40vh] xl:h-product lg:h-product object-contain rounded-lg mix-blend-multiply"
                alt="Organic Black Pepper by Harela"
              />
            </div>
          </motion.div>

          {/* PRODUCT DETAILS */}
          <motion.div variants={animation} className="col-span-1 h-full">
            {/* HEADING */}
            <div className="flex items-center justify-between mt-2">
              <div className="w-[90%] xl:w-4/5 lg:w-4/5">
                <h1 className="text-3xl font-fraunces-bold tracking-tight text-black">
                  Gold Whip Gas 4000G – 5.5L
                </h1>
                <h2 className="text-lg font-source font-normal text-headerGrey mt-1">
                  UPC: 150443984698
                </h2>
              </div>
              <div className="w-[10%] xl:w-1/5 lg:w-1/5 flex items-center justify-end">
                <Button className="shadow-none hover:shadow-none border border-greyBorder rounded-full p-3 bg-transparent hover:bg-black hover:bg-opacity-5 transition-all duration-300 ease-in-out">
                  <FaRegHeart className="w-5 h-5 text-black" />
                </Button>
              </div>
            </div>

            {/* REVIEWS AND PRICING */}
            <div className="mt-5">
              <p className="text-lg font-source font-normal text-textGrey">
                The Gold Whip Gas 4000G – 5.5L is a high-capacity nitrous oxide
                (N₂O) cylinder engineered for demanding catering and food
                service applications where extended usage and reliable
                performance are essential. With a net gas weight of 4000 g and a
                total cylinder volume of 5.5 litres, this tank delivers
                significantly more gas than standard smaller chargers, making it
                an efficient choice for professional kitchens, bakeries, and
                event catering services.
              </p>
            </div>

            {/* QUANTITY PRICING TABLE */}
            <div className="mt-6">
              <div className="rounded-2xl border border-greyBorder bg-gradient-to-br from-white via-white to-greyBg/60 p-5 shadow-sm">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-fraunces-bold text-black">
                    Quantity Pricing
                  </h3>
                  <p className="text-sm font-source text-headerGrey mt-1">
                    Buy more, save more. Prices shown are per unit.
                  </p>
                </div>

                <div className="mt-4 overflow-hidden rounded-xl border border-greyBorder bg-white">
                  <table className="w-full text-left">
                    <thead className="bg-greyBg">
                      <tr className="text-xs xl:text-sm lg:text-sm font-source font-semibold text-headerGrey">
                        <th className="px-4 py-3">Quantity</th>
                        <th className="px-4 py-3">Price / Unit</th>
                        <th className="px-4 py-3">You Save</th>
                        <th className="px-4 py-3 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody className="text-sm xl:text-base lg:text-base font-source text-black">
                      <tr className="border-t border-greyBorder">
                        <td className="px-4 py-3">1 – 4</td>
                        <td className="px-4 py-3">$99.00</td>
                        <td className="px-4 py-3 text-headerGrey">—</td>
                        <td className="px-4 py-3 text-right">$99 – $396</td>
                      </tr>
                      <tr className="border-t border-greyBorder bg-greyBg/50">
                        <td className="px-4 py-3">5 – 9</td>
                        <td className="px-4 py-3">$92.00</td>
                        <td className="px-4 py-3 text-green-700">Save 7%</td>
                        <td className="px-4 py-3 text-right">$460 – $828</td>
                      </tr>
                      <tr className="border-t border-greyBorder">
                        <td className="px-4 py-3">10 – 19</td>
                        <td className="px-4 py-3">$86.00</td>
                        <td className="px-4 py-3 text-green-700">Save 13%</td>
                        <td className="px-4 py-3 text-right">$860 – $1,634</td>
                      </tr>
                      <tr className="border-t border-greyBorder bg-greyBg/50">
                        <td className="px-4 py-3">20 – 49</td>
                        <td className="px-4 py-3">$79.00</td>
                        <td className="px-4 py-3 text-green-700">Save 20%</td>
                        <td className="px-4 py-3 text-right">
                          $1,580 – $3,871
                        </td>
                      </tr>
                      <tr className="border-t border-greyBorder">
                        <td className="px-4 py-3">50+</td>
                        <td className="px-4 py-3">$72.00</td>
                        <td className="px-4 py-3 text-green-700">Save 27%</td>
                        <td className="px-4 py-3 text-right">$3,600+</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* CART BTN AND QUANTITY */}
            <div className="flex items-center gap-3 mt-8">
              <QuantityBox
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
              <Button className="w-full shadow-none hover:shadow-none bg-black hover:bg-opacity-80 text-base font-source font-semibold text-white py-2.5 rounded-full normal-case">
                Add to cart
              </Button>
              <Button className="w-full py-2.5 bg-[#f57826] hover:bg-opacity-80 shadow-none hover:shadow-none text-white text-base font-source font-semibold rounded-full normal-case transition-all duration-300 ease-in-out">
                Buy it now
              </Button>
            </div>
          </motion.div>
        </motion.div>

        {/* YOU MAY ALSO LIKE */}
        <motion.div variants={animation} className="pt-6 xl:pt-14 lg:pt-14">
          <h2 className="text-3xl xl:text-4xl lg:text-4xl font-fraunces-bold tracking-tight text-black">
            You may also like
          </h2>
          <p className="text-xl font-source font-semibold text-headerGrey">
            Explore our unrivaled collection of must-have products!
          </p>
          <div className="mt-8 grid grid-cols-1 xl:grid-cols-4 lg:grid-cols-4 gap-5">
            {bestsellers.slice(0, 4).map((data) => (
              <div className="col-span-1" key={data.id}>
                <ProductBox
                  image={data.image}
                  name={data.name}
                  ratings={data.ratings}
                  ratingsPeople={data.ratingsPeople}
                  price={data.price}
                  mrpPrice={data.mrpPrice}
                />
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default ProductDetail;
